import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { H2 } from "../../../../components/text/headings";
import { CaretDown } from "@phosphor-icons/react";
import { useGetSupplierOverviewData } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { getFormattedNumValue, showErrorToast } from "../../../../utils";
import { ResponsiveLine } from "@nivo/line";
import * as React from "react";
import LocalSpinner from "../../../../components/LocalSpinner";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import { chartTheme, axisBaselineLayer } from "../components/Consumption/chartTheme";
import PillToggle from "../components/PillToggle";
import SupplierFlowSankey from "./SupplierFlowSankey";
import { ChartLine, FlowArrow } from "@phosphor-icons/react";

/**
 * Warning: Unlike elsewhere, this does not use the category, data quality, or year filters from the URL.
 * It handles year differently.
 */
const SupplierTrends = ({ filters, setFilters }) => {
  const toast = useToast();

  const [startYear, setStartYear] = useState(2021);
  const [endYear, setEndYear] = useState(2030);
  const [method, setMethod] = useState("Emission");
  const [chartData, setChartData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [view, setView] = useState("trend");
  const [flowYear, setFlowYear] = useState(new Date().getFullYear());

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  /**
   * Processes raw emission data and formats it for chart visualization based on selected scope and calculation method.
   * Creates three data series: actual emissions (historical), targets, and projections (future).
   */
  useEffect(() => {
    if (rawData && filters?.scope) {
      /**
       * Calculates emission values and intensity based on the selected scope
       * @param {Object} data - Yearly emission data for a specific year
       * @returns {Object} Formatted emission values with intensity calculations
       */
      const getData = (data) => {
        let emission = 0;
        let totalSpend = data?.totalSpend || 0;
        let projectedSpend = data?.projectedSpend || 0;
        let intensity = 0;
        let projectedEmission = 0;
        let projectedIntensity = 0;

        // Calculate emissions based on scope selection
        if (filters?.scope === "All Scopes") {
          emission = data?.totalEmissions || 0;
          projectedEmission = data?.projectedEmissions || 0;
        } else if (filters?.scope === "Scope 1 and 2") {
          emission = (data?.scope1Emissions || 0) + (data?.scope2Emissions || 0);
          projectedEmission = (data?.projectedScope1Emissions || 0) + (data?.projectedScope2Emissions || 0);
        } else if (filters?.scope === "Scope 3") {
          emission = data?.scope3Emissions || 0;
          projectedEmission = data?.projectedScope3Emissions || 0;
        }

        // Calculate intensity as emissions per spend (avoid division by zero)
        intensity = totalSpend ? emission / totalSpend : 0;
        projectedIntensity = projectedSpend ? projectedEmission / projectedSpend : 0;

        return {
          emission: emission,
          intensity: intensity,
          projectedEmissions: projectedEmission,
          projectedIntensity: projectedIntensity,
        };
      };

      const currentYear = new Date().getFullYear();

      const formattedData = [
        {
          id: "Supplier Emissions",
          // Only show historical data (up to current year)
          data: Object.entries(rawData)
            .filter(([year]) => parseInt(year) <= currentYear)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.intensity : getData(values)?.emission,
            })),
          color: "#63AD03",
        },
        {
          id: "Total Target",
          // Show targets for all years
          data: Object.entries(rawData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.totalIntensityTarget : values?.totalTarget,
          })),
          color: "#FF9500",
        },
        {
          id: "Scoped Target",
          // Show targets for all years
          data: Object.entries(rawData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.scopedIntensityTarget : values?.scopedEmissionTarget,
          })),
          color: "#FF9500",
        },
        {
          id: "Projected",
          // Show projections from previous year onwards (includes overlap year for continuity)
          data: Object.entries(rawData)
            // only show projections from previous year onwards + 3 years
            .filter(([year]) => parseInt(year) >= currentYear - 1 && parseInt(year) < currentYear + 3)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.projectedIntensity : getData(values)?.projectedEmissions,
            })),
          color: "#A6F334",
        },
      ];
      setChartData(formattedData);
    }
  }, [rawData, filters?.scope, method]);

  const { isFetching } = useGetSupplierOverviewData(years, filters?.categories, filters?.dataQuality, filters?.scope, {
    onError: (error) => {
      console.error("Error fetching supplier trends data:", error);
      showErrorToast(toast, "Failed to fetch supplier trends data");
    },
    onSuccess: (response) => {
      setRawData(response);
    },
    cacheTime: 60, // 1 minute
  });

  // Matches the carbon EmissionsTrends line style: solid actuals, dashed
  // target/projection, all with round caps. Series whose id contains "Target"
  // or "Projected" render dashed.
  const isDashed = (id) => /target|projected/i.test(id);
  // Fill the area under the ACTUAL (solid) series only — target/projection
  // lines stay unshaded. Reuses lineGenerator so the shaded top edge follows
  // the same monotone curve as the line, then closes down to the plot bottom.
  // Shade the gap between actuals and the Total Target — orange tint when
  // above target (bad), green tint when below (good). Straight-line segments
  // for the fill edges; the curved SupplierLines layer handles line rendering.
  const GapShade = ({ series, xScale, yScale, lineGenerator, innerHeight }) => {
    const currentYear = new Date().getFullYear();
    const actuals = series.find((s) => s.id === "Supplier Emissions");
    const target = series.find((s) => s.id === "Total Target");
    if (!actuals || !target) return null;
    const actualPts = actuals.data.filter((d) => d.data.y != null && parseInt(d.data.x) <= currentYear);
    const targetPts = target.data.filter((d) => d.data.y != null);
    if (actualPts.length < 2 || targetPts.length < 2) return null;

    const pairs = actualPts
      .map((a) => {
        const t = targetPts.find((tp) => String(tp.data.x) === String(a.data.x));
        return t ? { ax: xScale(a.data.x), ay: yScale(a.data.y), ty: yScale(t.data.y) } : null;
      })
      .filter(Boolean);
    if (pairs.length < 2) return null;

    // Build both edges using lineGenerator with the full point set so the
    // monotoneX tangents match the rendered lines exactly.
    const topPath = lineGenerator(pairs.map((p) => ({ x: p.ax, y: p.ay })));
    const botPath = lineGenerator([...pairs].reverse().map((p) => ({ x: p.ax, y: p.ty })));
    const last = pairs[pairs.length - 1];
    const first = pairs[0];
    // Strip the leading "Mx,y" from botPath so it can be appended after the L connector.
    const botSegment = botPath.replace(/^M[^A-Za-z]+/, "");
    const closedPath = `${topPath}L${last.ax},${last.ty}${botSegment}L${first.ax},${first.ay}Z`;

    // Clip each segment to its own x-column so it can be coloured independently.
    return (
      <g>
        {pairs.slice(0, -1).map((p0, i) => {
          const p1 = pairs[i + 1];
          const color = p0.ay > p0.ty || p1.ay > p1.ty ? "#E53E3E" : "#38A169";
          const clipId = `sc-gap-shade-${i}`;
          return (
            <g key={i}>
              <defs>
                <clipPath id={clipId}>
                  <rect x={p0.ax} y={-10} width={p1.ax - p0.ax + 1} height={innerHeight + 20} />
                </clipPath>
              </defs>
              <path d={closedPath} fill={color} fillOpacity={0.12} stroke="none" clipPath={`url(#${clipId})`} />
            </g>
          );
        })}
      </g>
    );
  };

  const SupplierAreas = ({ series, lineGenerator, xScale, yScale, innerHeight }) => (
    <g>
      {series.map((s) => {
        if (isDashed(s.id)) return null;
        const pts = s.data.filter((d) => d.data.y != null);
        if (pts.length === 0) return null;
        const topPath = lineGenerator(pts.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) })));
        const x0 = xScale(pts[0].data.x);
        const x1 = xScale(pts[pts.length - 1].data.x);
        return (
          <path
            key={s.id}
            d={`${topPath} L ${x1},${innerHeight} L ${x0},${innerHeight} Z`}
            fill={s.color}
            fillOpacity={0.12}
            stroke="none"
          />
        );
      })}
    </g>
  );
  const SupplierLines = ({ series, lineGenerator, xScale, yScale }) => (
    <g>
      {series.map((s) => (
        <path
          key={s.id}
          d={lineGenerator(s.data.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) })))}
          fill="none"
          stroke={s.color}
          strokeWidth={2}
          strokeDasharray={isDashed(s.id) ? "6 4" : undefined}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </g>
  );
  // White-fill circular points with the series colour as the stroke — the
  // carbon look (avoids Nivo's inconsistent pointBorderColor inheritor).
  const SupplierPoints = ({ series, xScale, yScale }) => (
    <g>
      {series.map((s) =>
        s.data.map((d, i) =>
          d.data.y == null ? null : (
            <circle
              key={`${s.id}-${i}`}
              cx={xScale(d.data.x)}
              cy={yScale(d.data.y)}
              r={4}
              fill="#FFFFFF"
              stroke={s.color}
              strokeWidth={2}
            />
          )
        )
      )}
    </g>
  );

  const isTrend = view === "trend";

  // Flow is a single-year snapshot for the year picked in the flow year filter,
  // using the overview per-year scope totals.
  const flowSnapshot = React.useMemo(() => {
    const v = rawData?.[flowYear];
    return {
      scope1: Number(v?.scope1Emissions) || 0,
      scope2: Number(v?.scope2Emissions) || 0,
      scope3: Number(v?.scope3Emissions) || 0,
    };
  }, [rawData, flowYear]);

  return (
    <Box>

      <Accordion allowToggle>
        <AccordionItem>

          <AccordionButton>
            <H2 fontSize="22px" fontWeight="700" color="darkTeal">Supplier Trends</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Flex justify="space-between" align="flex-start" gap={2} flexWrap="wrap">
              <Flex direction="column" gap={2}>
                <Flex gap={2} flexWrap="wrap">
                  {isTrend ? (
                    <>
                      <YearStartEndDropdown
                        setStartYear={setStartYear}
                        startYear={startYear}
                        setEndYear={setEndYear}
                        endYear={endYear}
                      />
                      <SupplierFiltersCatQualityYear filters={filters} setFilters={setFilters} showFilters={["scope"]} />
                    </>
                  ) : (
                    <SingleYearDropdown year={flowYear} setYear={setFlowYear} />
                  )}
                </Flex>
                {isTrend && (
                  <Flex justifyContent="start">
                    <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
                  </Flex>
                )}
              </Flex>
              <PillToggle
                options={[
                  { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
                  { label: "Flow", value: "flow", icon: <FlowArrow size={14} /> },
                ]}
                selected={view}
                setSelected={setView}
              />
            </Flex>

            {!isTrend && (
              <Box mt={4}>
                <SupplierFlowSankey
                  year={flowYear}
                  scope1={flowSnapshot?.scope1}
                  scope2={flowSnapshot?.scope2}
                  scope3={flowSnapshot?.scope3}
                  rootLabel="Attributed emissions"
                  subject="the supply chain"
                />
              </Box>
            )}

            {isTrend && (
            <>
            <Box mt={2}>
              <Text fontSize="body.m" fontWeight="600" color="gray.700">
                Supplier emissions over time
              </Text>
              <Text fontSize="body.xs" color="gray.500">
                {method === "Intensity" ? "Emissions intensity (tCO2e/£m)" : "Total attributed emissions (tCO2e)"} by
                year, {startYear} → {endYear}. Solid line = actuals; dashed = target and projection.
              </Text>
            </Box>

            {isFetching && <LocalSpinner />}

            <Box height="500px" width="100%">
              {chartData && chartData.length > 0 && !isFetching && (
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 30, right: 30, bottom: 56, left: 90 }}
                  xScale={{ type: "point" }}
                  yScale={{ type: "linear", min: "auto", max: "auto", stacked: false, reverse: false }}
                  curve="monotoneX"
                  theme={chartTheme}
                  axisBottom={{ legend: "Year", legendOffset: 42, legendPosition: "middle", tickPadding: 8 }}
                  axisLeft={{
                    legend: method === "Intensity" ? "Intensity (tCO2e/£m)" : "Total Emissions (tCO2e)",
                    legendOffset: -72,
                    legendPosition: "middle",
                    tickPadding: 6,
                  }}
                  enableGridX={false}
                  enablePoints={false}
                  colors={(d) => d.color}
                  lineWidth={2}
                  enableTouchCrosshair={true}
                  useMesh={true}
                  layers={["grid", "markers", "axes", axisBaselineLayer, SupplierAreas, GapShade, "crosshair", SupplierLines, SupplierPoints, "slices", "mesh"]}
                  tooltip={({ point }) => (
                    <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                      <Text fontWeight="700" color={point.color}>
                        {point.serieId}
                      </Text>
                      <Text color="gray.600">Year: {point.data.x}</Text>
                      <Text color="gray.600">{getFormattedNumValue(Number(point.data.y), 2)}</Text>
                    </Box>
                  )}
                />
              )}
            </Box>
            {/* Custom HTML legend below the chart (nivo's built-in legend
                collides with the bottom axis label). Dashed swatch for
                target/projection series. */}
            {chartData && chartData.length > 0 && !isFetching && (
              <Flex justify="center" wrap="wrap" gap={5} mb={2}>
                {chartData.map((s) => (
                  <Flex key={s.id} align="center" gap={2}>
                    <Box as="svg" width="16px" height="10px" viewBox="0 0 16 10" flexShrink={0}>
                      <line
                        x1="0"
                        y1="5"
                        x2="16"
                        y2="5"
                        stroke={s.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={isDashed(s.id) ? "4 3" : undefined}
                      />
                      {!isDashed(s.id) && <circle cx="8" cy="5" r="3" fill="#FFFFFF" stroke={s.color} strokeWidth="2" />}
                    </Box>
                    <Text fontSize="12px" fontWeight="600" color="#4A5568">
                      {s.id}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )}
            <Text fontSize="body.xs" color="gray.500" mt={2}>
              A falling line means supplier emissions are trending down. Compare actuals against the dashed target to
              see whether the supply chain is on track.
            </Text>
            </>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

    </Box>
  );
};

export const YearStartEndDropdown = ({ setStartYear, startYear, setEndYear, endYear }) => {
  const years = Array.from({ length: 10 }, (_, i) => 2021 + i);

  return (
    <Flex gap={2}>
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          borderRadius="full"
          border="1px solid"
          borderColor="gray.300"
          bg="transparent"
          color="gray.600"
          fontWeight="normal"
          rightIcon={<CaretDown size={14} />}
          _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        >
          From: {startYear || "Select year"}
        </MenuButton>
        <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
          {years.map((year) => (
            <MenuItem key={year} onClick={() => setStartYear(year)} fontWeight={startYear === year ? "bold" : "normal"}>
              {year}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          borderRadius="full"
          border="1px solid"
          borderColor="gray.300"
          bg="transparent"
          color="gray.600"
          fontWeight="normal"
          rightIcon={<CaretDown size={14} />}
          _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        >
          To: {endYear || "Select year"}
        </MenuButton>
        <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
          {years.map((year) => (
            <MenuItem key={year} onClick={() => setEndYear(year)} fontWeight={endYear === year ? "bold" : "normal"}>
              {year}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Flex>
  );
};

// Single-year pill used by the Flow view (a Sankey is one year at a time).
// Changing it re-snapshots the flow to that year.
export const SingleYearDropdown = ({ year, setYear }) => {
  const years = Array.from({ length: 10 }, (_, i) => 2021 + i);

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
      >
        Year: {year || "Select year"}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        {years.map((y) => (
          <MenuItem key={y} onClick={() => setYear(y)} fontWeight={year === y ? "bold" : "normal"}>
            {y}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export const ScopeDropdown = ({ selected, setSelected }) => {
  const options = ["All Scopes", "Scope 1 and 2", "Scope 3"];

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
      >
        {selected || "All Scopes"}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => setSelected(option)}
            fontWeight={selected === option ? "bold" : "normal"}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

// Emission / Intensity as a black-pill switch (matching the Annual/Quarterly
// toggle on the carbon footprint). Values stay "Emission"/"Intensity" — the
// consuming charts compare against those strings.
export const TrendCalculationMethodDropdown = ({ selected, setSelected }) => (
  <PillToggle
    options={[
      { label: "Emission", value: "Emission" },
      { label: "Intensity", value: "Intensity" },
    ]}
    selected={selected}
    setSelected={setSelected}
  />
);

export default SupplierTrends;
